import { useEffect, useRef, useState } from "react";
import RefreshOnNewBuildService from "../service/refreshOnBuildService";
import { DEFAULT_REFRESH_INTERVAL_MILLISECONDS } from "../utils/commonUtil";
/**
 * useRefreshCheck Hook
 *
 * A custom React Hook for periodically checking if a hard refresh of the web application is recommended
 * based on the comparison of the current bundle path with the previous bundle path.
 *
 * In production, this `src` will be of the form `/assets/index.[hash].js`,
 * which changes on every build.
 *
 * In development, Vite serves `/src/main.tsx` (unhashed), so the hook
 * will never trigger a refresh.
 */
const useRefreshCheck = (): {
  shouldHardRefresh: boolean;
  terminate: () => void;
} => {
  const [shouldHardRefresh, setShouldHardRefresh] = useState(false);
  const intervalRef = useRef<number | null>(null);
  const oldVersionRef = useRef<string>("");

  useEffect(() => {
    const refreshIntervalMilliseconds =
      Number(window?._env_?.REACT_APP_REFRESH_CHECK_INTERVAL_MILLISECONDS) ||
      DEFAULT_REFRESH_INTERVAL_MILLISECONDS;

    const startRefreshCheck = async () => {
      const { version } = await RefreshOnNewBuildService.tryGetVersion();
      oldVersionRef.current = version;

      intervalRef.current = window.setInterval(async () => {
        try {
          const refreshRequired =
            await RefreshOnNewBuildService.shouldForceRefresh(
              oldVersionRef.current
            );

          if (refreshRequired) {
            setShouldHardRefresh(true);
          }
        } catch (error) {
          console.error("Failed to check if a refresh is needed:", error);
        }
      }, refreshIntervalMilliseconds);
    };

    startRefreshCheck();

    return terminate;
  }, []);

  const terminate = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  return { shouldHardRefresh, terminate };
};
export { useRefreshCheck };
